import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import './ForumPost.css';
import './PageShell.css';

const POST_TEXT = {
  zh: {
    loading: '加载中...',
    forum: '论坛',
    deletePostConfirm: '确定删除这个帖子吗？',
    delete: '删除',
    cancel: '取消',
    postedAt: value => `发布于 ${value}`,
    reply: '回复',
    allReplies: count => `全部回复 (${count})`,
    writeReply: '发表回复',
    replyPlaceholder: '写下你的回复...',
    submitting: '提交中...',
    submitReply: '发表回复',
    loginPrompt: '登录后才能参与讨论',
    loginNow: '立即登录',
    justNow: '刚刚',
    minutesAgo: n => `${n}分钟前`,
    hoursAgo: n => `${n}小时前`,
    points: '积分',
    levels: { expert: '大神', pro: '达人', active: '活跃', new: '新人' },
  },
  en: {
    loading: 'Loading...',
    forum: 'Forum',
    deletePostConfirm: 'Delete this post?',
    delete: 'Delete',
    cancel: 'Cancel',
    postedAt: value => `Posted ${value}`,
    reply: 'Reply',
    allReplies: count => `All replies (${count})`,
    writeReply: 'Write a reply',
    replyPlaceholder: 'Write your reply...',
    submitting: 'Submitting...',
    submitReply: 'Post reply',
    loginPrompt: 'Log in to join the discussion.',
    loginNow: 'Log in',
    justNow: 'Just now',
    minutesAgo: n => `${n}m ago`,
    hoursAgo: n => `${n}h ago`,
    points: 'points',
    levels: { expert: 'Expert', pro: 'Pro', active: 'Active', new: 'New' },
  },
};

const CATEGORY_COPY = {
  en: {
    '经验分享': 'Experience sharing',
  },
};

function timeAgo(dateStr, text, language) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return text.justNow;
  if (diff < 3600) return text.minutesAgo(Math.floor(diff / 60));
  if (diff < 86400) return text.hoursAgo(Math.floor(diff / 3600));
  return new Date(dateStr).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US');
}

function levelForPoints(points, text) {
  if (points >= 500) return { label: text.levels.expert, color: '#f59e0b' };
  if (points >= 100) return { label: text.levels.pro, color: '#108a00' };
  if (points >= 20) return { label: text.levels.active, color: '#108a00' };
  return { label: text.levels.new, color: '#6b7280' };
}

function UserCard({ name, points, text }) {
  const level = levelForPoints(points, text);
  return (
    <div className="user-card">
      <div className="avatar">{name?.[0]?.toUpperCase()}</div>
      <div className="user-card-name">{name}</div>
      <div className="user-card-level" style={{ color: level.color }}>{level.label}</div>
      <div className="user-card-points">{points || 0} {text.points}</div>
    </div>
  );
}

export default function ForumPost() {
  const { id } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const text = POST_TEXT[language] || POST_TEXT.en;
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const replyRef = useRef(null);

  useEffect(() => {
    api.get(`/forum/posts/${id}`)
      .then(res => setPost(res.data))
      .catch(() => navigate('/forum'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/forum/posts/${id}/replies`, { content: reply });
      setPost(p => ({
        ...p,
        replies: [...p.replies, res.data],
        reply_count: p.reply_count + 1,
      }));
      setReply('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    await api.delete(`/forum/posts/${id}`);
    navigate(`/forum/categories/${post.category_id}`);
  };

  const handleDeleteReply = async (replyId) => {
    await api.delete(`/forum/replies/${replyId}`);
    setPost(p => ({
      ...p,
      replies: p.replies.filter(r => r.id !== replyId),
      reply_count: p.reply_count - 1,
    }));
  };

  if (loading) return <div className="loading">{text.loading}</div>;
  if (!post) return null;

  const localize = value => (language === 'en' ? CATEGORY_COPY.en[value] || value : value);

  return (
    <div className="page-shell-page forum-post-page">
      <div className="container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <div className="breadcrumb">
        <Link to="/forum">{text.forum}</Link>
        <span>/</span>
        <Link to={`/forum/categories/${post.category_id}`}>{post.category?.icon} {localize(post.category?.name)}</Link>
        <span>/</span>
        <span style={{ color: '#374151' }}>{post.title}</span>
      </div>

      <div className="post-detail-layout">
        <div>
          {/* 主帖 */}
          <div className="card post-main">
            <div className="post-header-bar">
              <h1 className="post-main-title">{post.title}</h1>
              <div className="post-actions">
                <span className="post-meta-item">👁 {post.view_count}</span>
                <span className="post-meta-item">💬 {post.reply_count}</span>
                {user?.id === post.author_id && (
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteConfirmVisible(true)}>{text.delete}</button>
                )}
              </div>
            </div>
            {deleteConfirmVisible && (
              <div className="post-delete-confirm" role="status">
                <span>{text.deletePostConfirm}</span>
                <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>{text.delete}</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDeleteConfirmVisible(false)}>{text.cancel}</button>
              </div>
            )}
            <div className="post-body-layout">
              <UserCard name={post.author_name} points={post.author_points} text={text} />
              <div className="post-body">
                <div className="post-content">{post.content}</div>
                <div className="post-footer-meta">
                  {text.postedAt(timeAgo(post.created_at, text, language))}
                  {user && (
                    <button className="reply-btn" onClick={() => replyRef.current?.focus()}>{text.reply}</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 回复列表 */}
          {post.replies.length > 0 && (
            <div className="replies-section">
              <div className="replies-header">{text.allReplies(post.reply_count)}</div>
              {post.replies.map((r, i) => (
                <div key={r.id} className="reply-item card">
                  <div className="reply-floor">#{i + 1}</div>
                  <div className="post-body-layout">
                    <UserCard name={r.author_name} points={r.author_points} text={text} />
                    <div className="post-body">
                      <div className="post-content">{r.content}</div>
                      <div className="post-footer-meta">
                        {timeAgo(r.created_at, text, language)}
                        {user?.id === r.author_id && (
                          <button className="delete-reply-btn" onClick={() => handleDeleteReply(r.id)}>{text.delete}</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 回复框 */}
          {user ? (
            <div className="card reply-box">
              <h3>{text.writeReply}</h3>
              <form onSubmit={handleReply}>
                <textarea
                  ref={replyRef}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder={text.replyPlaceholder}
                  rows={5}
                  style={{ width: '100%', marginTop: 12 }}
                  className="reply-textarea"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button type="submit" className="btn btn-primary" disabled={submitting || !reply.trim()}>
                    {submitting ? text.submitting : text.submitReply}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '24px' }}>
              <p style={{ color: '#6b7280', marginBottom: 12 }}>{text.loginPrompt}</p>
              <Link to="/login" className="btn btn-primary">{text.loginNow}</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
}
